import React, { useId } from 'react'
import { Loader2 } from 'lucide-react'

const confirmStyles = {
  danger: {
    backgroundColor: '#ef4444',
    color: '#fff',
    border: 'none',
  },
  primary: {
    backgroundColor: '#2563eb',
    color: '#fff',
    border: 'none',
  },
  neutral: {
    backgroundColor: 'transparent',
    color: 'var(--color-text-secondary)',
    border: '1px solid var(--color-border)',
  },
}

/**
 * Platform confirmation modal (overlay). Use variant="danger" for destructive actions.
 */
export default function ConfirmDialog({
  open,
  onClose,
  title,
  description,
  highlight,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  busyLabel = 'Working…',
  variant = 'primary',
  busy = false,
  error,
  onConfirm,
}) {
  const titleId = useId()

  if (!open) return null

  const confirmStyle = confirmStyles[variant] || confirmStyles.primary

  const handleOverlayClick = () => {
    if (!busy) onClose()
  }

  const handleConfirm = () => {
    if (!busy) void onConfirm()
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.6)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1110,
        padding: '20px',
      }}
      onClick={handleOverlayClick}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: 'var(--color-surface)',
          borderRadius: 'var(--radius-xl)',
          border: '1px solid var(--color-border)',
          boxShadow: 'var(--shadow-xl)',
          maxWidth: '440px',
          width: '100%',
        }}
      >
        <div style={{ padding: '22px 24px 12px' }}>
          <h2
            id={titleId}
            style={{
              margin: 0,
              fontSize: '18px',
              fontWeight: 700,
              color: 'var(--color-text-primary)',
              letterSpacing: '-0.02em',
            }}
          >
            {title}
          </h2>
          {description ? (
            <p style={{ margin: '12px 0 0', fontSize: '13px', color: 'var(--color-text-muted)', lineHeight: 1.5 }}>
              {description}
            </p>
          ) : null}
          {highlight ? (
            <p
              style={{
                margin: '12px 0 0',
                fontSize: '15px',
                fontWeight: 600,
                color: 'var(--color-text-primary)',
                wordBreak: 'break-word',
              }}
            >
              {highlight}
            </p>
          ) : null}
          {error ? (
            <p style={{ margin: '12px 0 0', fontSize: '13px', color: '#ef4444' }} role="alert">
              {error}
            </p>
          ) : null}
        </div>
        <div
          style={{
            padding: '16px 24px 22px',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '10px',
            flexWrap: 'wrap',
          }}
        >
          <button
            type="button"
            disabled={busy}
            onClick={onClose}
            style={{
              padding: '10px 18px',
              borderRadius: '8px',
              border: '1px solid var(--color-border)',
              backgroundColor: 'transparent',
              color: 'var(--color-text-secondary)',
              fontWeight: 600,
              fontSize: '14px',
              cursor: busy ? 'not-allowed' : 'pointer',
            }}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={handleConfirm}
            style={{
              padding: '10px 18px',
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: '14px',
              cursor: busy ? 'wait' : 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              ...confirmStyle,
            }}
          >
            {busy && <Loader2 size={16} style={{ animation: 'spin 0.8s linear infinite' }} />}
            {busy ? busyLabel : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
